/**
 * Unified create-form config.
 *
 * One config type drives every CRM create surface — the generic
 * schema-driven form (contacts, companies, deals, custom `org_*` types) and
 * bespoke flows like the booking-link form. The root
 * (`CreateSlideoutProvider`) renders a `CrmCreateForm` for each config pushed
 * onto its stack; the form reads only this type.
 *
 * Callers with a flat `createProperties` / `createAssociations` list (the
 * list page, pipeline, association pickers, entity registry) build their
 * config with `buildGenericCreateConfig` rather than authoring sections.
 */

import type { ReactNode } from 'react';

import type { DraftParentContext } from '@/lib/crm/core/draftEntities';

/**
 * Per-field render callback for `custom` fields — the escape hatch for
 * inputs that don't fit the schema-driven `PropertyInput` dispatch. Kept
 * narrow on purpose.
 */
export type CrmCreateCustomRender = (
  value: unknown,
  onChange: (next: unknown) => void,
  disabled: boolean
) => ReactNode;

interface CrmCreateFieldBase {
  /**
   * Optional predicate. When it returns false for the current form values,
   * the field is hidden and its value is reset to the initial value so the
   * submitted payload matches what the user could see. Values that already
   * equal the initial value are left alone (and the reset never marks the
   * form dirty).
   */
  condition?: (values: Record<string, unknown>) => boolean;
}

/** A schema-backed property rendered through the canonical `PropertyInput`. */
export interface CrmCreatePropertyField extends CrmCreateFieldBase {
  kind: 'property';
  /** Property type name, e.g. `ahoy_name`. Looked up in the entity's schema. */
  property: string;
  /** Override the schema label. `''` renders no label row at all. */
  label?: string;
  /** Override the schema's required flag (for validation and the indicator). */
  required?: boolean;
  /** Help text shown below the field while it has no visible error. */
  helpText?: string;
}

/** An association picker rendered through `CrmAssociationField`. */
export interface CrmCreateAssociationField extends CrmCreateFieldBase {
  kind: 'association';
  /** Association type name, e.g. `ahoy_contact_to_company`. */
  associationType: string;
}

/**
 * A field whose value lives in the form but whose input is caller-rendered.
 * The value is passed through to `onSubmit` untouched — no property/
 * association splitting, no empty-value stripping.
 */
export interface CrmCreateCustomField extends CrmCreateFieldBase {
  kind: 'custom';
  /** Form key for the value (e.g. `_team_member_ids`). */
  property: string;
  /** Label text. `''` renders no label row. */
  label: string;
  required?: boolean;
  helpText?: string;
  render: CrmCreateCustomRender;
}

export type CrmCreateField =
  | CrmCreatePropertyField
  | CrmCreateAssociationField
  | CrmCreateCustomField;

export interface CrmCreateSection {
  id: string;
  /** Section heading. Omit for an unlabelled group (the generic form). */
  title?: string;
  description?: string;
  fields: CrmCreateField[];
  /**
   * When the config's `sectionStyle` is `'accordion'`, this section starts
   * expanded. Single-expand: only the first section flagged true is honored.
   */
  defaultExpanded?: boolean;
}

export interface CrmCreateConfig {
  /** Org the record is created in; scopes schema lookups and submission. */
  orgId: number;
  /**
   * Entity type name, e.g. `ahoy_contact` or `org_project`. A plain string
   * because the generic list page passes whatever type its config carries.
   */
  entityType: string;
  /** Slideout title and breadcrumb label when nested. */
  title: string;
  /** Small label rendered above the title (icon + category word). */
  eyebrow?: ReactNode;
  /** Primary button label. Defaults to `'Create'`. */
  submitLabel?: string;
  /** Sections rendered top to bottom, every field full width. */
  sections: CrmCreateSection[];
  /** `'flat'` (default) stacks headed groups; `'accordion'` collapses them. */
  sectionStyle?: 'flat' | 'accordion';
  /**
   * Values the form starts with. Property fields without an entry fall back
   * to the schema default; owner associations default to the current user.
   */
  initialValues?: Record<string, unknown>;
  /**
   * Cross-field synchronous validation on top of the schema-derived rules.
   * Returns property name → error message; an empty record means valid.
   */
  validate?: (values: Record<string, unknown>) => Record<string, string>;
  /**
   * Submit handler. Receives the cleaned payload: property values with
   * empties stripped, `associations` / `draft_entities` /
   * `overwrite_targets` when present, and every `custom` field verbatim.
   * Resolving with `{ entity_id }` lets `onCreated` receive the new id.
   * Reject to keep the form open with the error surfaced.
   */
  onSubmit: (data: Record<string, unknown>) => Promise<unknown>;
  /** Called after a successful submit when `onSubmit` resolved an entity id. */
  onCreated?: (entityId: string) => void;
  /** Show the "& add another" action. Defaults to true. */
  showCreateAnother?: boolean;
  /**
   * Apply the generic create path's ontology gate — only entity kinds in
   * `CREATABLE_ENTITY_KINDS` (party / case / asset) may be created. Set by
   * `buildGenericCreateConfig`, because the type-agnostic "+ Add" affordances
   * must not offer types the ontology never meant to be spawned that way.
   *
   * A bespoke config is the opposite situation: a page deliberately wired a
   * create flow for one specific type (a booking link is `scheduling`-kind,
   * not party/case/asset), so it does not opt in. The per-user permission
   * check and the schema's `user_creatable` flag still apply to every config.
   */
  enforceGenericCreatableKinds?: boolean;
  /**
   * Fires on every property field change (name, value). Side-effect only —
   * the contacts page uses it to prefetch the email dedup lookup.
   */
  onPropertyChange?: (propertyTypeName: string, value: unknown) => void;
  /**
   * Set when this form is a depth-1 nested create launched from a parent
   * form's association picker: the reverse association becomes a read-only
   * parent-link banner, deal auto-fill is suppressed, and nested pickers
   * hide their own "create new" option.
   */
  draftParentContext?: DraftParentContext;
  /**
   * Fills the footer's secondary slot in place of the default Cancel button.
   * Receives the current form values (e.g. to open a preview).
   */
  renderFooterSecondaryAction?: (values: Record<string, unknown>) => ReactNode;
}

/** Shape a successful `onSubmit` may resolve with so `onCreated` can fire. */
export interface CrmCreateResult {
  entity_id: string;
}

export function isCrmCreateResult(value: unknown): value is CrmCreateResult {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { entity_id?: unknown }).entity_id === 'string'
  );
}
